import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import Icon from '../components/Icon';

const CATEGORIES = ['Strength', 'Cardio', 'Flexibility', 'Balance', 'Plyometrics', 'Other'];
const MUSCLE_GROUPS = ['Chest', 'Back', 'Shoulders', 'Arms', 'Core', 'Legs', 'Glutes', 'Full Body'];

function VideoModal({ url, onClose }) {
  const isYoutube = url.includes('youtube.com') || url.includes('youtu.be');
  let embedUrl = url;
  if (isYoutube) {
    const id = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/)?.[1];
    if (id) embedUrl = 'https://www.youtube.com/embed/' + id;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}>
      <div style={{ width: '100%', maxWidth: 600 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 8 }}>
            <Icon name="icon-close.png" size={28} alt="Close" />
          </button>
        </div>
        <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 12, overflow: 'hidden' }}>
          <iframe
            src={embedUrl}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Exercise video"
          />
        </div>
      </div>
    </div>
  );
}

export default function Exercises() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [filterMuscle, setFilterMuscle] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [form, setForm] = useState({ name: '', category: '', muscle_group: '', instructions: '', video_url: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/exercises').then(setExercises).finally(() => setLoading(false));
  }, []);

  function openAdd() { setEditing(null); setForm({ name: '', category: '', muscle_group: '', instructions: '', video_url: '' }); setShowForm(true); }
  function openEdit(ex) { setEditing(ex); setForm({ name: ex.name, category: ex.category||'', muscle_group: ex.muscle_group||'', instructions: ex.instructions||'', video_url: ex.video_url||'' }); setShowForm(true); }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const updated = await api.put('/exercises/' + editing.id, form);
        setExercises(prev => prev.map(ex => ex.id === updated.id ? updated : ex));
      } else {
        const created = await api.post('/exercises', form);
        setExercises(prev => [...prev, created]);
      }
      setShowForm(false);
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this exercise?')) return;
    await api.delete('/exercises/' + id);
    setExercises(prev => prev.filter(ex => ex.id !== id));
  }

  const filtered = exercises.filter(ex => {
    const matchSearch = ex.name.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCat || ex.category === filterCat;
    const matchMuscle = !filterMuscle || ex.muscle_group === filterMuscle;
    return matchSearch && matchCat && matchMuscle;
  });

  return (
    <div>
      {videoUrl && <VideoModal url={videoUrl} onClose={() => setVideoUrl('')} />}

      <div className="page-header">
        <h1>
          <Icon name="icon-dumbbell.png" />
          Exercises
        </h1>
        <button className="icon-btn" onClick={openAdd}>
          <Icon name="icon-add.png" alt="Add" />
        </button>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          <input className="form-input" placeholder="Search exercises..." value={search} onChange={e => setSearch(e.target.value)} />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <select className="form-select" value={filterCat} onChange={e => setFilterCat(e.target.value)}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="form-select" value={filterMuscle} onChange={e => setFilterMuscle(e.target.value)}>
              <option value="">All Muscles</option>
              {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {showForm && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 12 }}>{editing ? 'Edit Exercise' : 'New Exercise'}</div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Muscle Group</label>
                  <select className="form-select" value={form.muscle_group} onChange={e => setForm(f => ({...f, muscle_group: e.target.value}))}>
                    <option value="">Select</option>
                    {MUSCLE_GROUPS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Video Link (paste a link from your website or YouTube)</label>
                <input className="form-input" type="url" placeholder="https://..." value={form.video_url} onChange={e => setForm(f => ({...f, video_url: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Instructions</label>
                <textarea className="form-textarea" value={form.instructions} onChange={e => setForm(f => ({...f, instructions: e.target.value}))} />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" type="submit" disabled={saving} style={{ flex: 1 }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button className="btn btn-ghost" type="button" style={{ flex: 1 }} onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="loading-spinner"><span className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Icon name="icon-dumbbell.png" size={48} style={{ opacity: 0.3 }} />
            <p>{search || filterCat || filterMuscle ? 'No exercises match your filters' : 'No exercises yet'}</p>
            {!search && !filterCat && !filterMuscle && (
              <button className="btn btn-primary" style={{ width: 'auto', padding: '0 24px' }} onClick={openAdd}>Add First Exercise</button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.map(ex => (
              <div key={ex.id} className="card">
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{ex.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, display: 'flex', gap: 8 }}>
                      {ex.category && <span>{ex.category}</span>}
                      {ex.muscle_group && <span>· {ex.muscle_group}</span>}
                    </div>
                    {ex.instructions && <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 6, lineHeight: 1.4 }}>{ex.instructions}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {ex.video_url && (
                      <button className="icon-btn" onClick={() => setVideoUrl(ex.video_url)} title="Watch">
                        <Icon name="icon-play.png" alt="Play" />
                      </button>
                    )}
                    <button className="icon-btn" onClick={() => openEdit(ex)}>
                      <Icon name="icon-edit.png" alt="Edit" />
                    </button>
                    <button className="icon-btn" onClick={() => handleDelete(ex.id)}>
                      <Icon name="icon-delete.png" alt="Delete" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
